import {
  Text,
  useDisclosure,
  Icon,
  DrawerBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  TableContainer,
  Badge,
  ModalFooter,
  useToast,
} from '@chakra-ui/react'
import { FaRegArrowAltCircleRight } from 'react-icons/fa'
import { Button } from '../../../../../components/Button'
import { useMutation, useQuery } from 'react-query'
import { api } from '../../../../../services/api'
import React, { useState } from 'react'
import * as T from '../../../../../components/Table'
import { formatPrice } from '../../../../../utils/formatPrice'

const colors = {
  true: 'green',
  false: 'red',
}

async function onConfirmEmail({ numeroNota }) {
  const response = await api.post('https://api.innovationbrindes.com.br/api/site/v2/orcamento-crm/envia-confirmacao-estoque', {
    numero_nota: numeroNota,
  })

  return response.data
}

async function onSolicitarEmail({ numeroNota }) {
  const response = await api.post('https://api.innovationbrindes.com.br/api/site/v2/orcamento-crm/solicita-confirmacao-estoque', {
    numero_nota: numeroNota,
  })

  return response.data
}

async function onConfirmStock({ numeroNota }) {
  const response = await api.get(`/site/v2/orcamento-crm/busca-itens-confirmacao-estoque?numero_nota=${numeroNota}`)

  return response.data.map((item) => ({
    ...item,
    quantidade_negociada: new Intl.NumberFormat('pt-BR').format(item.quantidade_negociada),
    quantidade_estoque: new Intl.NumberFormat('pt-BR').format(item.quantidade_estoque),
    valor_unitario: formatPrice(item.valor_unitario),
    valor: formatPrice(item.valor),
    status: item.status ? 'Confirmado' : 'Não confirmado',
    originalStatus: item.status,
    colorStatus: colors[item.status],
  }))
}

export function ConfirmarEstoque({ numeroNota, enviarAprovacao }) {
  const { onOpen, isOpen, onClose } = useDisclosure()
  const toast = useToast({
    position: 'top-right',
    duration: 5000,
    isClosable: true,
  })

  const [updateStock, setUpdateStock] = useState(false)

  const {
    data: itensStock,
    isRefetching,
    isLoading,
  } = useQuery({
    queryKey: ['confirmarEstoque', { numeroNota: numeroNota }],
    queryFn: () => onConfirmStock({ numeroNota: numeroNota }),
    enabled: updateStock,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      setUpdateStock(false)

      onOpen()
    },
    onError: () => {
      setUpdateStock(false)
    },
  })

  const { mutateAsync: confirmEmail, isLoading: confirmEmailLoading } = useMutation({
    mutationFn: onConfirmEmail,
    onSuccess: () => {
      toast({
        title: 'E-mail enviado com sucesso',
        status: 'success',
      })
    },
    onError: (data) => {
      console.log(data.response)
      toast({
        title: 'Erro ao enviar e-mail',
        description: data.response.data.mensagem,
        status: 'error',
      })
    },
  })

  const { mutateAsync: solicitaEmail, isLoading: solicitaEmailLoading } = useMutation({
    mutationFn: onSolicitarEmail,
    onSuccess: (data) => {
      toast({
        title: 'E-mail solicitado com sucesso',
        description: data.mensagem,
        status: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao solicitar',
        status: 'error',
      })
    },
  })

  async function handleConfirmEmail() {
    await confirmEmail({ numeroNota: numeroNota })
    await enviarAprovacao()
  }

  async function handleSolicitarEmail() {
    await solicitaEmail({ numeroNota: numeroNota })
  }

  async function handleConfirmStock() {
    setUpdateStock(true)
  }

  const hasFalse = itensStock?.some((item) => item.originalStatus === false)

  const isTrue = itensStock?.every((item) => item.originalStatus === true)

  React.useEffect(() => {
    if (!isOpen) {
      setUpdateStock(false)
    }
  }, [isOpen])

  return (
    <>
      <Button mx="auto" isLoading={isRefetching || isLoading} onClick={handleConfirmStock}>
        Enviar para o Cliente
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center" textTransform="uppercase">
            Confirmação de Estoque
          </ModalHeader>

          <DrawerBody w="100%" mx="auto" display="flex" flexDir="column" alignItems="center" gap={4}>
            <TableContainer w="100%">
              <T.Table>
                <T.Thead>
                  <T.Tr>
                    <T.Th>Código</T.Th>
                    <T.Th>Valor unitário</T.Th>
                    <T.Th>Cor</T.Th>
                    <T.Th>Qtd. Negociada</T.Th>
                    <T.Th>Estoque</T.Th>
                    <T.Th>Status</T.Th>
                  </T.Tr>
                </T.Thead>
                <T.Tbody>
                  {itensStock?.map((item, index) => (
                    <T.Tr key={index}>
                      <T.Td py={1}>{item.codigo_produto}</T.Td>
                      <T.Td py={1}>{item.valor_unitario}</T.Td>
                      <T.Td py={1}>{item.descricao_cor}</T.Td>
                      <T.Td py={1}>{item.quantidade_negociada}</T.Td>
                      <T.Td py={1}>{item.quantidade_estoque}</T.Td>
                      <T.Td py={1}>
                        <Badge colorScheme={item.colorStatus}>{item.status}</Badge>
                      </T.Td>
                    </T.Tr>
                  ))}
                </T.Tbody>
              </T.Table>
            </TableContainer>
          </DrawerBody>

          <ModalFooter>
            <Button onClick={handleSolicitarEmail} disabled={!hasFalse} bgColor="blue.500" size="sm" mr={2} isDisabled={solicitaEmailLoading}>
              Solicitar por E-mail
            </Button>
            <Button
              onClick={handleConfirmEmail}
              disabled={!isTrue}
              bgColor="blue.500"
              size="sm"
              mr={2}
              isLoading={confirmEmailLoading}
              isDisabled={confirmEmailLoading}
            >
              Enviar
            </Button>
            <Button onClick={onClose} bgColor="red.500" size="sm">
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
